import type { PropsWithChildren } from 'react';
import React, { Suspense } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import {
    isProjectMappingInHelpCenterEnabled,
    isCustomPagesUiEnabled,
    isHideVsaEndpointsEnabled,
} from '@helpcenter/feature-flags';
import { NewGlobalBGColor } from '@helpcenter/styles/src/global';
import { sizes } from '@helpcenter/styles/src/sizes-viewport';
import ErrorScreen from 'assets/images/project-mapping-error.svg';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { useLazyLoadQuery } from 'react-relay';
import { usePathParam, useRouter } from 'react-resource-router';
import type { State } from 'state';
import { getHasSiteAdministerAccess } from 'state/selectors/user';
import { useMediaQuery } from 'view/help-center/use-screen-size/use-screen-size';
import EmptyState from '@atlaskit/empty-state';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import LinkIcon from '@atlaskit/icon/core/link';
import PagesIcon from '@atlaskit/icon/core/pages';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import WrenchIcon from '@atlaskit/icon-lab/core/wrench';
import { TOP_NAVIGATION_HEIGHT, BANNER_HEIGHT } from '@atlaskit/navigation-system/legacy/css-variables';
import { SpotlightManager } from '@atlaskit/onboarding';
import { Main, Content, PageLayout, TopNavigation } from '@atlaskit/page-layout';
import { Box, media, xcss } from '@atlaskit/primitives';
import { getIsHCCustomizationEnabled, getJsmAIConfigEnabled } from '@atlassian/help-center-common-util/env';
import { isPremiumUpsellExperimentEnabled } from '@atlassian/help-center-common-util/premium-upsell-experiment';
import { HelpCenterTypeProvider, useHelpCenterType } from '../../contexts/help-center-type-context';
import { SIDEBAR_WIDTH } from '../constants';
import { Header } from '../header';
import type { NavigationContentItem } from '../sidebar';
import { Sidebar } from '../sidebar';
import messages from './messages';

export interface StateProps {
    hasSiteAdministerAccess?: boolean;
}
export interface Props extends StateProps {
    featureName: string;
    hideSidebar?: boolean;
}

const AdminManagementLayoutContentBase: React.FC<PropsWithChildren<Props>> = ({
    children,
    featureName,
    hasSiteAdministerAccess,
    hideSidebar,
}) => {
    di(
        useRouter,
        usePathParam,
        getJsmAIConfigEnabled,
        isProjectMappingInHelpCenterEnabled,
        isCustomPagesUiEnabled,
        useLazyLoadQuery,
        Sidebar
    );
    const [{ route }, { push }] = useRouter();
    const { formatMessage } = useIntl();
    const [helpCenterId = ''] = usePathParam('helpcenterId');
    const { name: routeName } = route;
    const { isHelpHub } = useHelpCenterType();
    const rovoServiceAgentRouteName = 'rovoServiceAgentSettings';
    const rovoServiceAgentUrl = `/${helpCenterId}/rovo-agent`;
    const virtualAgentRouteName = 'virtualAgentSettings';
    const virtualAgentUrl = `/${helpCenterId}/virtual-agent`;
    const mapProjectsScreenUrl = `/${helpCenterId}/projects`;
    const mapPagesScreenUrl = `/${helpCenterId}/pages`;
    const editProjectDetailsScreenUrl = `/${helpCenterId}/details`;
    const navigationContentItems: NavigationContentItem[] = [
        {
            onClick: () => push(editProjectDetailsScreenUrl) as void,
            text: formatMessage(messages.helpcenterDetailsItem),
            isVisible: isProjectMappingInHelpCenterEnabled(),
            isSelected: routeName === 'edit-details',
            iconBefore: <WrenchIcon label={formatMessage(messages.helpcenterDetailsItem)} />,
        },
        {
            onClick: () => push(mapProjectsScreenUrl) as void,
            text: FeatureGates.checkGate('itsm-project-renaming')
                ? formatMessage(messages.helpcenterManageProjectsItemGalaxia)
                : formatMessage(messages.helpcenterManageProjectsItem),
            isSelected: routeName === 'manage-projects',
            isVisible: isProjectMappingInHelpCenterEnabled(),
            iconBefore: (
                <LinkIcon
                    label={
                        FeatureGates.checkGate('itsm-project-renaming')
                            ? formatMessage(messages.helpcenterManageProjectsItemGalaxia)
                            : formatMessage(messages.helpcenterManageProjectsItem)
                    }
                />
            ),
        },
    ];

    // The manage-pages page is only an upsell for non-premium users.
    // So we only show it for premium users or users with billing permission.
    if (getIsHCCustomizationEnabled() || hasSiteAdministerAccess) {
        navigationContentItems.push({
            onClick: () => push(mapPagesScreenUrl) as void,
            text: formatMessage(messages.helpcenterManagePagesItem),
            isSelected: routeName === 'manage-pages',
            isVisible: isCustomPagesUiEnabled() && !isHelpHub,
            iconBefore: <PagesIcon label={formatMessage(messages.helpcenterManagePagesItem)} />,
        });
    }

    // Rovo Agent
    // For Help Hubs: gated behind enable_rovo_in_help_hubs
    // For Help Centers: gated behind jsm_help_center_one-click_rovo_agent
    navigationContentItems.push({
        onClick: () => push(rovoServiceAgentUrl) as void,
        text: formatMessage(messages.helpcenterRovoServiceAgentItem),
        isSelected: routeName === rovoServiceAgentRouteName,
        isVisible: isHelpHub
            ? FeatureGates.checkGate('enable_rovo_in_help_hubs')
            : FeatureGates.checkGate('jsm_help_center_one-click_rovo_agent'),
        iconBefore: <RovoIcon label={formatMessage(messages.helpcenterRovoServiceAgentItem)} />,
    });
    // Virtual Service Agent

    if (
        !isHideVsaEndpointsEnabled() &&
        !isPremiumUpsellExperimentEnabled({
            hasSiteAdministerAccess,
            isHCCustomizationEnabled: getIsHCCustomizationEnabled(),
        })
    ) {
        navigationContentItems.push({
            onClick: () => push(virtualAgentUrl) as void,
            text: formatMessage(messages.helpcenterVsaReportingItem),
            isSelected: routeName === virtualAgentRouteName,
            isVisible: getJsmAIConfigEnabled() && !isHelpHub,
            iconBefore: <AiChatIcon label={formatMessage(messages.helpcenterVsaReportingItem)} />,
        });
    }

    const renderError = () => {
        return (
            <EmptyState
                header={formatMessage(messages.errorTitle)}
                description={formatMessage(messages.errorDescription)}
                imageUrl={ErrorScreen}
            />
        );
    };

    const packageName = (): string => {
        switch (routeName) {
            case 'edit-details':
                return 'editDetails';
            case 'manage-projects':
                return 'manageProjects';
            case 'manage-pages':
                return 'managePages';
            case 'rovoServiceAgentSettings':
                return 'rovoServiceAgentSettings';
            case 'virtualAgentSettings':
                return 'virtualAgentSettings';
            case 'reporting':
                return 'reporting';
            default:
                return 'unknown';
        }
    };

    const isTablet = !useMediaQuery(`(max-width: ${sizes.xsmall}px)`);
    return (
        <SpotlightManager blanketIsTinted>
            <Box xcss={pageLayoutWrapper} testId="admin-layout-management-page-wrapper">
                <PageLayout>
                    <TopNavigation isFixed={false}>
                        <Header />
                    </TopNavigation>
                    <Content>
                        {isTablet && !hideSidebar && (
                            <Sidebar
                                width={SIDEBAR_WIDTH}
                                navigationContentItems={navigationContentItems}
                                featureName={featureName}
                            />
                        )}
                        <Box xcss={bodyContainer}>
                            <Main>
                                <ErrorBoundary
                                    packageName={packageName()}
                                    id={'admin-layout-management-page'}
                                    renderError={renderError}
                                >
                                    <Box xcss={[container, !hideSidebar && containerWithMaxWidth]}>{children}</Box>
                                </ErrorBoundary>
                            </Main>
                        </Box>
                        <NewGlobalBGColor />
                    </Content>
                </PageLayout>
            </Box>
        </SpotlightManager>
    );
};

const selector = createSelector(
    (state: State) => {
        return {
            hasSiteAdministerAccess: getHasSiteAdministerAccess(state),
        };
    },
    ({ hasSiteAdministerAccess }) => ({ hasSiteAdministerAccess })
);

const AdminManagementLayoutContent = connect(selector)(AdminManagementLayoutContentBase);

export const AdminManagementLayout: React.FC<PropsWithChildren<Props>> = ({
    children,
    featureName,
    hideSidebar = false,
}) => {
    return (
        <Suspense fallback={null}>
            <HelpCenterTypeProvider>
                <AdminManagementLayoutContent featureName={featureName} hideSidebar={hideSidebar}>
                    {children}
                </AdminManagementLayoutContent>
            </HelpCenterTypeProvider>
        </Suspense>
    );
};

export default AdminManagementLayout;

const container = xcss({
    margin: '0 auto',
    paddingBottom: 'space.050',
    paddingInline: 'space.600',
    [media.above.lg]: {
        paddingInline: 'space.0',
    },
});

const containerWithMaxWidth = xcss({
    maxWidth: '1160px',
});

const pageLayoutWrapper = xcss({
    overflowAnchor: 'none',
    height: '100%',
});

const bodyContainer = xcss({
    overflow: 'auto',
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
    height: `calc(100vh - (${TOP_NAVIGATION_HEIGHT} + ${BANNER_HEIGHT}))`,
    width: '100%',
});
